import React, { useEffect, useRef, useState } from 'react';
import Footer from '../components/Footer';
import { supabase } from '../supabase';
import './Events.css';

const WIXBASE = 'https://static.wixstatic.com/media/';

function FadeUp({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`fade-up ${visible ? 'fade-up--visible' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

const DEFAULT_EVENTS = [
  {
    id: 1,
    title: 'Share Your Sound Competition 2026',
    description: 'Join the international Share Your Sound Competition for free! Showcase your musical talent to a global audience with the possibility to win up to $100 scholarships. Open to all skill levels and instruments. Deadline: May 25th, 11:59 PM EST.',
    event_month: 'May', event_day: '25', event_year: '2026',
    tags: ['Competition', 'Free to Enter', 'International', '$100 Scholarship'],
    status: 'upcoming', cta_label: 'Register Free',
    cta_url: 'mailto:shareyoursound01@gmail.com', is_featured: true,
    img: WIXBASE + 'bc004372f26546f4bf47b65a537c2d7f.jpg',
  },
  {
    id: 2,
    title: 'ECHO Interview Series',
    description: 'ECHO Music Empowerment is hosting a musician interview series. Share your experience pursuing music , whether as a career or just for fun. Live Zoom webinar where students worldwide can ask questions. Looking for high school, undergraduate, professional, or recreational musicians with a story to share.',
    event_month: 'Ongoing', event_day: '-', event_year: '2026',
    tags: ['Interview Series', 'Live Webinar', 'Global'],
    status: 'open', cta_label: 'Express Interest',
    cta_url: 'mailto:shareyoursound01@gmail.com', is_featured: false,
    img: WIXBASE + '11062b_ecb7c5279341479ca327aa4ac59c2da3~mv2.jpg',
  },
];

const PAST_EVENTS = [
  {
    id: 'p1',
    title: 'Detroit Public Library: Intro to Music Camp',
    date: 'January 18, 2026',
    description: 'A free one-day program for students in grades K-6, hosted with the Detroit Public Library. Kids learned music theory through trivia, Kahoot, and games, took home a free recorder or maraca, and performed a final piece for their parents. No music experience necessary.',
    tags: ['Detroit Public Library', 'K-6', 'Free Instruments'],
    images: [
      '/events-images/dpl-jan2026-flyer.png',
    ],
  },
  {
    id: 'p2',
    title: 'Detroit Public Library: Intro to Music Camp (Fall)',
    date: 'October 19, 2025',
    description: 'A return engagement with the Detroit Public Library bringing free music education to students in grades K-6. Attendees learned music theory through trivia and games, took home a free recorder or maraca, and performed for their families at the end of the session.',
    tags: ['Detroit Public Library', 'K-6', 'Free Instruments'],
    images: [
      '/events-images/dpl-oct2026-flyer.png',
    ],
  },
  {
    id: 'p3',
    title: 'Detroit Public Library: Winter Music Workshop',
    date: 'Winter 2026',
    description: 'A hands-on winter session at the Detroit Public Library where ECHO tutors led young students through interactive music theory lessons using note recognition games and staff notation, with crafts and activities to keep the learning fun.',
    tags: ['Detroit Public Library', 'Music Theory', 'Workshop'],
    images: [
      '/events-images/dpl-winter-photo.jpg',
    ],
  },
  {
    id: 'p4',
    title: 'ECHO x Novi Public Library: Introduction to Music Camp',
    date: 'August 22, 2025',
    description: 'A one-day free program for students in Grades K-8, hosted and planned entirely by Novi High School students. Attendees learned music theory through trivia, Kahoot, and games, and performed a final piece for their parents. All attendees received a free recorder.',
    tags: ['Novi Public Library', 'K-8', 'Student-Led'],
    images: [
      '/events-images/novi-itm-fall2025-flyer.png',
    ],
  },
  {
    id: 'p5',
    title: 'We Rock the Spectrum: Music & Sensory Camp',
    date: 'April 3, 2026',
    description: 'A music enrichment camp at We Rock the Spectrum, designed to be welcoming for students of all abilities. Kids participated in hands-on music activities, crafts, and games led by ECHO volunteer tutors in a relaxed, sensory-friendly environment.',
    tags: ['We Rock the Spectrum', 'Inclusive', 'Hands-On'],
    images: [
      '/events-images/wrts-photo1.png',
      '/events-images/wrts-photo2.png',
      '/events-images/wrts-photo3.jpg',
      '/events-images/wrts-photo4.jpg',
      '/events-images/wrts-photo5.jpg',
    ],
  },
  {
    id: 'p6',
    title: 'ECHO Masterclass with Majestica Lor',
    date: 'May 19, 2025',
    description: 'An online masterclass featuring Majestica Lor, an accomplished cellist who teaches at the Wisconsin Youth Symphony Orchestras Music Makers Program and studied cello performance at the University of Wisconsin-Madison Mead Witter School of Music. Students joined live to learn innovative and artistic techniques for young musicians.',
    tags: ['Masterclass', 'Cello', 'Online'],
    images: [
      '/events-images/masterclass-majestica-flyer.jpg',
    ],
    imageFit: 'contain',
  },
  {
    id: 'p7',
    title: 'Share Your Sound International Competition',
    date: 'Due May 25',
    description: 'A free international competition open to any high school student with a musical passion. Participants submitted a performance recording (up to 10 minutes) and answered the prompt "How has music shaped your journey or helped you overcome challenges?" Winners received $100 scholarships.',
    tags: ['Competition', 'International', '$100 Scholarship', 'Free Entry'],
    images: [
      '/events-images/competition-flyer.jpg',
    ],
  },
  {
    id: 'p8',
    title: 'ECHO General Information & Free Online Lessons',
    date: 'Ongoing',
    description: 'ECHO is a youth organization dedicated to bringing music to students of all ages, offering free online music tutoring from top high school musicians across more than 20 instruments and voice, including flute, violin, guitar, piano, percussion, and even Ethiopian drum.',
    tags: ['Free Lessons', 'All Instruments', 'Online'],
    images: [
      '/events-images/echo-general-flyer.png',
    ],
  },
];

export default function Events({ navigate }) {
  const [events, setEvents] = useState(DEFAULT_EVENTS);

  useEffect(() => {
    supabase.from('events').select('*').order('created_at', { ascending: false }).then(({ data }) => {
      if (data && data.length) setEvents(data);
    });
  }, []);

  const statusLabel = { upcoming: 'Upcoming', open: 'Open', past: 'Past' };
  const statusColor = { upcoming: '#c8873a', open: '#6b8f71', past: '#7a7068' };

  return (
    <div className="events-page">
      {/* Hero */}
      <div className="page-hero events-hero">
        <div className="page-hero-ghost">Events</div>
        <div className="events-hero-bg">
          <img src={WIXBASE + '11062b_6100a7dce7cb44039baefc0d6a720deb~mv2.jpg'} alt="" className="events-hero-bgimg" />
          <div className="events-hero-overlay" />
        </div>
        <div className="events-hero-content">
          <div className="section-tag" style={{ color: 'var(--purple-light)' }}>Community</div>
          <h1>Upcoming <em>Events</em></h1>
          <p>Fundraisers, concerts, competitions, and community outreach events hosted by ECHO Music Tutoring.</p>
        </div>
      </div>

      {/* Events list */}
      <section className="events-list">
        {events.map((ev, i) => (
          <FadeUp key={ev.id || i} delay={i * 100} className={`event-row ${ev.is_featured ? 'event-row--featured' : ''}`}>
            <div className="event-date-block">
              <span className="event-month">{ev.event_month}</span>
              <span className="event-day">{ev.event_day}</span>
              <span className="event-year">{ev.event_year}</span>
            </div>
            <div className="event-img-wrap">
              {ev.img && <img src={ev.img} alt={ev.title} className="event-img" />}
            </div>
            <div className="event-content">
              <div className="event-meta">
                <span className="event-status" style={{ color: statusColor[ev.status] }}>
                  ● {statusLabel[ev.status]}
                </span>
                {ev.is_featured && <span className="event-featured-badge">Featured</span>}
              </div>
              <h2 className="event-title">{ev.title}</h2>
              <p className="event-desc">{ev.description}</p>
              <div className="event-tags">
                {(ev.tags || []).map((t, ti) => (
                  <span key={ti} className="event-tag">{t}</span>
                ))}
              </div>
              <a
                href={ev.cta_url || 'mailto:shareyoursound01@gmail.com'}
                className="btn-primary event-cta"
                target={ev.cta_url?.startsWith('http') ? '_blank' : undefined}
                rel="noopener noreferrer"
              >
                {ev.cta_label || 'Learn More'} →
              </a>
            </div>
          </FadeUp>
        ))}
      </section>

      {/* Past Events */}
      <section className="past-events-section">
        <FadeUp>
          <div className="section-tag">Look Back</div>
          <h2 className="section-h2">Past <em>Events</em></h2>
          <p className="past-events-intro">
            A glimpse at the camps, workshops, masterclasses, and competitions ECHO has hosted across our community.
          </p>
        </FadeUp>
        <div className="past-events-grid">
          {PAST_EVENTS.map((ev, i) => (
            <FadeUp key={ev.id} delay={(i % 3) * 100} className="past-event-card">
              <div className={`past-event-images ${ev.images.length > 1 ? 'past-event-images--multi' : ''} ${ev.imageFit === 'contain' ? 'past-event-images--contain' : ''}`}>
                {ev.images.map((src, idx) => (
                  <img
                    key={idx}
                    src={src}
                    alt={`${ev.title} ${idx + 1}`}
                    className="past-event-img"
                    style={ev.imageFit === 'contain' ? { objectFit: 'contain' } : undefined}
                  />
                ))}
              </div>
              <div className="past-event-body">
                <div className="past-event-date">{ev.date}</div>
                <h3 className="past-event-title">{ev.title}</h3>
                <p className="past-event-desc">{ev.description}</p>
                <div className="event-tags">
                  {ev.tags.map((t, ti) => (
                    <span key={ti} className="event-tag">{t}</span>
                  ))}
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Interview CTA */}
      <section className="interview-cta">
        <FadeUp className="interview-cta-inner">
          <div className="interview-left">
            <div className="section-tag">Get Involved</div>
            <h2 className="section-h2" >
              Want to be <em>interviewed?</em>
            </h2>
            <p>We're looking for musicians of all backgrounds and experience levels. Your story can inspire students across the globe.</p>
            <a href="mailto:shareyoursound01@gmail.com" className="btn-primary" style={{ marginTop: "1.5rem" }}>
              Contact Us to Apply
            </a>
          </div>
          <div className="interview-right">
            {[
              'Live Zoom webinar where students worldwide can ask questions',
              'Get featured on the ECHO platform and social channels',
              'Share your musical journey and inspire others',
              'Support young musicians and aspiring performers globally',
              'Open to all levels: high school, undergrad, professional, recreational',
            ].map((item, i) => (
              <div key={i} className="interview-benefit">
                <span className="interview-diamond">◆</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </FadeUp>
      </section>

      <Footer navigate={navigate} />
    </div>
  );
}
